import React, { useContext, useMemo, useState } from 'react';
import styles from './AddCompositionUnit.module.css';
import AuthenticationContext from 'config/context/authenticationContext';
import ModalInputField from 'components/shared/modal/ModalInputField';
import ModalDropdown from 'components/shared/modal/ModalDropdown';
import { useAddCompositionContext } from 'features/compositions/context/AddCompositionContext';
import CompositionRecord from '../composition-record/CompositionRecord';
import { PlusIcon } from 'features/compositions/assets/icons/icons';
import AddRecord from '../composition-record/AddRecord';
import { validateCompositionName } from 'utils/validations';

const AddCompositionUnit = ({ data, index, recordTypes }) => {
  const [nra, setNra] = useState(1);
  const { permissionsList } = useContext(AuthenticationContext);
  const { setTable, removeTableById, tablesLength, allCompositionsList, customTypes } = useAddCompositionContext();
  const [draggedItemIndex, setDraggedItemIndex] = useState(null);

  const normalizeAIFlag = (fields) => {
    const firstIndexPosition = fields.findIndex(f => f.is_index);

    return fields.map((f, i) => {
      if (!f.is_index) {
        return { ...f, ai: false };
      }

      if (i === firstIndexPosition) {
        return { ...f };
      }

      return { ...f, ai: false };
    });
  };

  const normalizeIndexFlag = (fields) => {
    return fields.map(f => {
      if (f.is_index) {
        return { ...f, is_array: false }; // Ako je index true, array je false
      }
      return f;
    });
  };

  const normalizeFields = (fields) => {
    const aiNormalized = normalizeAIFlag(fields);
    const indexNormalized = normalizeIndexFlag(aiNormalized);
    return indexNormalized;
  };

  const handleDragStart = (idx) => {
    setDraggedItemIndex(idx);
  };

  const handleDragOver = (e, overIdx) => {
    e.preventDefault();
    if (draggedItemIndex === null || draggedItemIndex === overIdx) return;

    setTable((prevTable) => {
      const newFields = [...prevTable.fields];
      const [moved] = newFields.splice(draggedItemIndex, 1);
      newFields.splice(overIdx, 0, moved);
      const normalized = normalizeFields(newFields);
      setDraggedItemIndex(overIdx);
      return { ...prevTable, fields: normalized };
    }, index);
  };

  const handleDragEnd = () => {
    setDraggedItemIndex(null);
  };

  const handleItemChange = (idx, key, value) => {
    setTable((prevTable) => {
      const updated = [...prevTable.fields];
      updated[idx] = { ...updated[idx], [key]: value };
      const normalized = normalizeFields(updated);
      return { ...prevTable, fields: normalized };
    }, index);
  };

  const handleDeleteItem = (idx) => {
    setTable((prevTable) => {
      const updated = [...prevTable.fields];
      if (!updated[idx]?.new) return prevTable;
      updated.splice(idx, 1);
      return { ...prevTable, fields: updated };
    }, index);
  };

  const customTypeNames = useMemo(() => customTypes.map(type => type.name), [customTypes]);

  return (
    <div className={styles.add_composition_unit}>
      <div className={styles.header}>
        <ModalInputField
          autoFocus={true}
          type="text"
          placeholder="Composition name"
          value={data.name}
          setValue={(val) => setTable((prev) => ({ ...prev, name: val }), index)}
          validator={(val) => validateCompositionName(val, allCompositionsList)}
        />
        <ModalInputField
          type="text"
          placeholder="Description (optional)"
          value={data.description || ''}
          setValue={(val) => setTable((prev) => ({ ...prev, description: val }), index)}
        />
        <ModalDropdown
          title={data.permission || 'Composition permission'}
          options={permissionsList}
          setOption={(val) =>
            setTable((prev) => ({ ...prev, permission: val }), index)
          }
        />
      </div>

      <div className={styles.records_area}>
        <div className={styles.records_container}>
          <div className={styles.records_header}>
            <span></span>
            <span>Index</span>
            <span>AI</span>
            <span>Array</span>
            <span>Name</span>
            <span>Type</span>
            <span>Default value</span>
          </div>

          <div className={styles.records}>
            {data.fields.map((item, idx) => (
              <CompositionRecord
                key={`${item.name}-${idx}`}
                item={item}
                index={idx}
                isDragged={idx === draggedItemIndex}
                onDragStart={handleDragStart}
                onDragOver={(e) => handleDragOver(e, idx)}
                onDragEnd={handleDragEnd}
                onDrop={handleDragEnd}
                onChange={handleItemChange}
                onDelete={handleDeleteItem}
                dataTypes={recordTypes}
                customTypes={customTypes}
                isPrimaryKeyRow={idx === (data?.fields || []).findIndex(f => f.is_index)}
              />
            ))}
          </div>
        </div>
      </div>

      {
        nra === 1 && <div className={styles.actions}>
          <div className={styles.actions_left}>
            <button onClick={() => setNra(2)} className={styles.add_btn}>Add new record <PlusIcon /></button>
          </div>
          {tablesLength() > 1 && <button onClick={() => removeTableById(index)} className={styles.remove_comp_btn}>Remove</button>}
        </div>
      }
      {
        nra === 2 && <div className={styles.actions}>
          <div className={styles.add_record_actions}>
            <button onClick={() => setNra(3)} className={styles.add_record_btn}>Add field</button>
            <button onClick={() => setNra(4)} className={styles.add_record_btn}>Add index</button>
            <button onClick={() => setNra(5)} className={styles.add_record_btn}>Add array</button>
            {customTypeNames.length > 0 && <button onClick={() => setNra(6)} className={styles.add_record_btn}>Add object</button>}
          </div>
        </div>
      }
      <div className={styles.actions}>
        {nra === 3 && <AddRecord
          table={data}
          setTable={(updated) => setTable(() => updated, index)}
          setNra={setNra}
          isIndex={false}
          isArray={false}
          dataTypes={recordTypes}
          customTypes={customTypes}
        />}
        {nra === 4 && <AddRecord
          table={data}
          setTable={(updated) => setTable(() => updated, index)}
          setNra={setNra}
          isIndex={true}
          isArray={false}
          dataTypes={recordTypes}
          customTypes={customTypes}
        />}
        {nra === 5 &&
          <AddRecord
            table={data}
            setTable={(updated) => setTable(() => updated, index)}
            setNra={setNra}
            isIndex={false}
            isArray={true}
            dataTypes={recordTypes}
            customTypes={customTypes}
          />}
        {nra === 6 &&
          <AddRecord
            table={data}
            setTable={(updated) => setTable(() => updated, index)}
            setNra={setNra}
            isIndex={false}
            isArray={false}
            isObject={true}
            dataTypes={customTypeNames}
            customTypes={customTypes}
          />}
      </div>
    </div>
  );
};

export default AddCompositionUnit;
